import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, Database, Fingerprint, KeyRound, Loader2, Lock, LogOut, Moon, ShieldCheck, Sun, Wifi, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Label } from '../components/ui/Label';
import { useAuth } from '../contexts/AuthContext';
import { useStudents } from '../contexts/StudentsContext';
import { useTheme } from '../contexts/ThemeContext';
import { initialsOf } from '../utils/format';
import { accountProfile } from '../utils/profile';
import { scorePassword } from '../utils/security';
import { SUPABASE_PROJECT_REF, testSupabaseConnection } from '../utils/supabase';
export function Settings() {
  const {
    user,
    logout,
    lock,
    changeMasterPassword
  } = useAuth();
  const {
    students,
    syncState
  } = useStudents();
  const {
    theme,
    setTheme
  } = useTheme();
  const navigate = useNavigate();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{
    ok: boolean;
    detail: string;
  } | null>(null);
  const profile = accountProfile(user);
  const connected = syncState === 'live';
  const runConnectionTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const result = await testSupabaseConnection();
      setTestResult(result);
      if (result.ok) toast.success('Supabase reachable');else toast.error('Supabase unreachable', {
        description: result.detail
      });
    } finally {
      setTesting(false);
    }
  };
  const handleChangePassword = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError('');
    if (next.length < 6) {
      setError('The new master password needs at least 6 characters.');
      return;
    }
    if (next !== confirm) {
      setError('The new passwords do not match.');
      return;
    }
    setSaving(true);
    try {
      await changeMasterPassword(current, next);
      toast.success('Master password updated');
      setCurrent('');
      setNext('');
      setConfirm('');
    } catch (changeError) {
      setError(changeError instanceof Error ? changeError.message : 'Could not update the master password.');
    } finally {
      setSaving(false);
    }
  };
  const handleLogout = async () => {
    try {
      await logout();
      toast.success('Logged out safely.');
    } catch (logoutError) {
      toast.error(logoutError instanceof Error ? logoutError.message : 'Could not log out.');
    }
    navigate('/', {
      replace: true
    });
  };
  return <div className="mx-auto w-full max-w-4xl space-y-5" data-id="element-635" data-mp="cpmkx23zl3iq">
      <section className="glass flex flex-col gap-4 rounded-3xl p-6 sm:flex-row sm:items-center" data-id="element-636" data-mp="hrbyopilfvai">
        {profile.avatarUrl ? <img src={profile.avatarUrl} alt={profile.name} className="h-16 w-16 rounded-2xl border border-[var(--glass-border)] object-cover" data-id="element-637" data-mp="226i3wt0tnb8c" /> : <span className="grid h-16 w-16 place-items-center rounded-2xl brand-gradient text-lg font-semibold text-white" data-id="element-638" data-mp="1sc8bxcfskey5">
            {initialsOf(profile.name)}
          </span>}
        <div className="min-w-0 flex-1" data-id="element-639" data-mp="2l15hceywmh9h">
          <h2 className="truncate text-lg font-semibold tracking-tight text-foreground" data-id="element-640" data-mp="2fzg54vzy3xjt">{profile.name}</h2>
          <p className="truncate text-sm text-muted-foreground" data-id="element-641" data-mp="2b8hm3onkolcv">{profile.email}</p>
          <p className="mt-1.5 inline-flex items-center gap-1.5 rounded-full bg-emerald-500/12 px-2.5 py-0.5 text-xs font-medium text-emerald-700 dark:text-emerald-300" data-id="element-642" data-mp="26ag1gzai0dza">
            <ShieldCheck className="h-3 w-3" data-id="element-643" data-mp="33rn7qcw9l0ro" />
            Google account verified via Supabase
          </p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row" data-id="element-644" data-mp="2jh3vvfciv7kf">
          <Button variant="outline" className="h-9 rounded-xl" onClick={() => {
          lock();
          navigate('/security', {
            replace: true
          });
        }} data-id="element-645" data-mp="2jfj0xomapmm1">
            <Lock className="mr-1.5 h-4 w-4" data-id="element-646" data-mp="29l0u4all1ay0" />
            Lock app
          </Button>
          <Button variant="ghost" className="h-9 rounded-xl text-destructive" onClick={handleLogout} data-id="element-647" data-mp="2ejtxvbb2hnkz">
            <LogOut className="mr-1.5 h-4 w-4" data-id="element-648" data-mp="329eswskhwbam" />
            Log out
          </Button>
        </div>
      </section>

      <div className="grid gap-4 lg:grid-cols-2" data-id="element-649" data-mp="2shijilcykyr3">
        <section className="glass rounded-3xl p-6" data-id="element-650" data-mp="2sgqxo295yfe0">
          <h3 className="text-sm font-semibold tracking-tight text-foreground" data-id="element-651" data-mp="2xglok5sxv4z2">Appearance</h3>
          <p className="mt-1 text-xs text-muted-foreground" data-id="element-652" data-mp="3l6692t4qqaag">Choose how Student Recorder looks on this device.</p>
          <div className="mt-4 grid grid-cols-2 gap-3" data-id="element-653" data-mp="3pwmp8kzy4a0i">
            {(['light', 'dark'] as const).map(option => <button key={option} type="button" onClick={() => setTheme(option)} aria-pressed={theme === option} className={`flex flex-col items-start gap-3 rounded-2xl border p-4 text-left transition-all ${theme === option ? 'border-brand bg-white/70 shadow-sm dark:bg-white/10' : 'border-[var(--glass-hairline)] hover:bg-white/50 dark:hover:bg-white/5'}`} data-id="element-654" data-mp="1eh5mlxymmgom">
                <span className={`grid h-9 w-9 place-items-center rounded-xl ${option === 'dark' ? 'bg-slate-900 text-slate-100' : 'bg-amber-100 text-amber-700'}`} data-id="element-655" data-mp="qljikh1k42xt">
                  {option === 'dark' ? <Moon className="h-4 w-4" data-id="element-656" data-mp="2fy545w167nq2" /> : <Sun className="h-4 w-4" data-id="element-657" data-mp="2fxbrkefj6400" />}
                </span>
                <span className="text-sm font-medium capitalize text-foreground" data-id="element-658" data-mp="2by30jhbvkk85">{option} mode</span>
              </button>)}
          </div>
        </section>

        <section className="glass rounded-3xl p-6" data-id="element-659" data-mp="1dlibfmdgfqo2">
          <div className="flex items-start justify-between gap-3" data-id="element-660" data-mp="1dk75ph0g88ub">
            <div className="min-w-0" data-id="element-661" data-mp="1ik20vwr9bkl6">
              <h3 className="text-sm font-semibold tracking-tight text-foreground" data-id="element-662" data-mp="1il39luksvuzq">Supabase connection</h3>
              <p className="mt-1 truncate text-xs text-muted-foreground" data-id="element-663" data-mp="1mldq4167y3su">Project {SUPABASE_PROJECT_REF}</p>
            </div>
            <span className={`inline-flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${connected ? 'bg-emerald-500/12 text-emerald-700 dark:text-emerald-300' : 'bg-amber-500/12 text-amber-700 dark:text-amber-300'}`} data-id="element-664" data-mp="pu375o24aza6">
              <Wifi className="h-3 w-3" data-id="element-665" data-mp="74dd5366ihjb" />
              {connected ? 'Connected' : 'Not connected'}
            </span>
          </div>

          <ul className="mt-4 space-y-3 text-sm" data-id="element-666" data-mp="b4x7jyzdb71r">
            <li className="flex items-center gap-3 rounded-2xl border border-[var(--glass-hairline)] p-3" data-id="element-667" data-mp="3nqx5f9zsbtk">
              <span className="grid h-9 w-9 place-items-center rounded-xl bg-emerald-500/12 text-emerald-600" data-id="element-668" data-mp="3uuv3e7ybbu0x">
                <Database className="h-4 w-4" data-id="element-669" data-mp="3qqvnnrkuwz8m" />
              </span>
              <span className="min-w-0 flex-1" data-id="element-670" data-mp="3ltn4porvwr6b">
                <span className="block text-sm font-medium text-foreground" data-id="element-671" data-mp="3lsu7beetfe0u">Postgres table</span>
                <span className="block truncate text-xs text-muted-foreground" data-id="element-672" data-mp="3gvbvng9eykp5">
                  <span className="font-mono" data-id="element-673" data-mp="3c2iqwsdy3cd2">students</span> · {students.length} row{students.length === 1 ? '' : 's'}
                </span>
              </span>
            </li>
            <li className="flex items-center gap-3 rounded-2xl border border-[var(--glass-hairline)] p-3" data-id="element-674" data-mp="333f69jz4qowg">
              <span className="grid h-9 w-9 place-items-center rounded-xl bg-indigo-500/12 text-indigo-600" data-id="element-675" data-mp="2y2hq1cxok4xl">
                <Fingerprint className="h-4 w-4" data-id="element-676" data-mp="2t5raqrtp9dd8" />
              </span>
              <span className="min-w-0 flex-1" data-id="element-677" data-mp="kv8fgopgkk7l">
                <span className="block text-sm font-medium text-foreground" data-id="element-678" data-mp="kwjgigrirdv0">Account ID</span>
                <span className="block truncate font-mono text-xs text-muted-foreground" data-id="element-679" data-mp="pubadgbq5a1u">{profile.id || '—'}</span>
              </span>
            </li>
          </ul>

          <Button variant="outline" className="mt-4 h-9 w-full rounded-xl" onClick={runConnectionTest} disabled={testing} data-id="element-680" data-mp="gtdczgvsk0s8">
            {testing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" data-id="element-681" data-mp="6widovrp1wcw" /> : <Wifi className="mr-2 h-4 w-4" data-id="element-682" data-mp="6y1xuxgh0rjy" />}
            {testing ? 'Testing…' : 'Test Supabase connection'}
          </Button>

          {testResult && <p className={`mt-3 flex items-start gap-2 rounded-2xl p-3 text-xs ${testResult.ok ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300' : 'bg-amber-500/10 text-muted-foreground'}`} data-id="element-683" data-mp="3tclc8u5g46qf">
              {testResult.ok ? <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0" data-id="element-684" data-mp="30pbtjv1d9vq0" /> : <XCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600" data-id="element-685" data-mp="2vrjt2cmnp1a7" />}
              {testResult.detail}
            </p>}
        </section>
      </div>

      <section className="glass rounded-3xl p-6" data-id="element-686" data-mp="35ndeymm9m74n">
        <h3 className="flex items-center gap-2 text-sm font-semibold tracking-tight text-foreground" data-id="element-687" data-mp="3jnxw7ecj7uw1">
          <KeyRound className="h-4 w-4 text-brand" data-id="element-688" data-mp="3ep4hxjpf7zut" />
          Master password
        </h3>
        <p className="mt-1 text-xs text-muted-foreground" data-id="element-689" data-mp="3tdr7ee8ri7zr">
          Only a salted SHA-256 digest is stored on your Supabase account — never the password itself.
        </p>

        <form onSubmit={handleChangePassword} className="mt-5 grid gap-4 sm:grid-cols-3" noValidate data-id="element-690" data-mp="3offv7wuzvled">
          <div className="space-y-2" data-id="element-691" data-mp="66ylex8w6o3c">
            <Label htmlFor="current-master" data-id="element-692" data-mp="27zkrou71dhp">Current password</Label>
            <Input id="current-master" type="password" value={current} onChange={event => setCurrent(event.target.value)} autoComplete="current-password" className="h-10 rounded-xl" data-id="element-693" data-mp="25msoiuq1mqx" />
          </div>
          <div className="space-y-2" data-id="element-694" data-mp="248dzlz3szcz">
            <Label htmlFor="new-master" data-id="element-695" data-mp="3td4dry947zxu">New password</Label>
            <Input id="new-master" type="password" value={next} onChange={event => setNext(event.target.value)} autoComplete="new-password" className="h-10 rounded-xl" data-id="element-696" data-mp="3tajf1i44604b" />
            {next.length > 0 && <p className="text-xs text-muted-foreground" data-id="element-697" data-mp="kuzpat2nizu0">Strength: {scorePassword(next).label}</p>}
          </div>
          <div className="space-y-2" data-id="element-698" data-mp="gxk0mz2kte4c">
            <Label htmlFor="confirm-master" data-id="element-699" data-mp="35na4trrx2viv">Confirm new password</Label>
            <Input id="confirm-master" type="password" value={confirm} onChange={event => setConfirm(event.target.value)} autoComplete="new-password" className="h-10 rounded-xl" data-id="element-700" data-mp="31lg7ntgxk9ng" />
          </div>

          {error && <p role="alert" className="rounded-xl bg-destructive/10 px-3 py-2 text-sm font-medium text-destructive sm:col-span-3" data-id="element-701" data-mp="1q3kal2g842qb">
              {error}
            </p>}

          <div className="sm:col-span-3" data-id="element-702" data-mp="1zsu6yui3o7k8">
            <Button type="submit" disabled={saving} className="h-10 rounded-xl brand-gradient px-5 text-white hover:opacity-95" data-id="element-703" data-mp="1zw86luzdq2nz">
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" data-id="element-704" data-mp="28shablue3nfx" />}
              {saving ? 'Updating…' : 'Update master password'}
            </Button>
          </div>
        </form>
      </section>
    </div>;
}